import { backgroundCheckStatusUpdate, getBackgroundCheckHistoryStorage } from '@/backend/features/background-check';
import { getBackgroundTaskStateStorage } from '@juvare/contact-management/backend/features/background-task-state';
import { isProdEnvironment } from '@juvare/contact-management/backend/utils';
import { BackgroundTaskDef, StorageContext } from '@juvare/platform-app/App';
import { BackgroundCheckRequest, BackgroundCheckResponse } from '@juvare/platform-app/Integration';
import { AppState } from '@juvare/platform-sdk-react/types';
import { millisecondsInHour } from 'date-fns/constants';
import { ParsedBackgroundCheck, cleanResults, parseBackgroundCheck } from '../platform-functions/utils';
import { BackgroundCheckStatus, BackgroundCheckType } from '../types';

const prodInterval = millisecondsInHour;
const taskName = 'backgroundCheckStatusUpdate';

export const backgroundCheckStatusUpdateTask: BackgroundTaskDef<AppState> = {
    callback: (context) => {
        console.log('Starting backgroundCheckStatusUpdate background task...');
        const backgroundTaskStateStorage = getBackgroundTaskStateStorage(context);
        const backgroundTaskStateResponse = backgroundTaskStateStorage.list({
            filter: [{ column: 'taskName', op: '=', value: taskName }],
            limit: 1,
        });
        const backgroundTaskState = backgroundTaskStateResponse.total > 0 ? backgroundTaskStateResponse.rows[0] : null;
        // default bounce timestamp - 1 hour
        const runTimestampInMilliSeconds = Date.now();

        const lastRunTimestampInSeconds = backgroundTaskState?.columns.lastRunTimestamp ?? runTimestampInMilliSeconds - prodInterval;

        if (isProdEnvironment(context.environment) && lastRunTimestampInSeconds > runTimestampInMilliSeconds - prodInterval) {
            return;
        }

        backgroundCheckStatusUpdate(context, 'System');

        backgroundTaskStateStorage.post({
            action: 'create-or-update',
            uniqueColumn: 'taskName',
            data: {
                taskName: taskName,
                lastRunTimestamp: runTimestampInMilliSeconds,
            },
        });
        console.log('Finished backgroundCheckStatusUpdate background task.');
    },
    delaySeconds: 300,
};

interface CreateBackgroundCheckHistoryParams {
    type: BackgroundCheckType;
    accountId: string;
    requestedBy: string;
    backgroundCheckStatus?: BackgroundCheckStatus;
    request?: BackgroundCheckRequest;
    response?: BackgroundCheckResponse;
    nextStatus: BackgroundCheckStatus;
    message?: string;
    stopCheckingForResultsHistoryId?: string;
    responseXml?: string;
    internalStatusChange?: boolean;
    isTestMode?: boolean;
    isUsingQuota?: boolean;
    isManualChange?: boolean;
}

export function createBackgroundCheckHistory(
    context: StorageContext,
    {
        request,
        requestedBy,
        type,
        accountId,
        backgroundCheckStatus,
        response,
        nextStatus,
        message,
        responseXml,
        stopCheckingForResultsHistoryId,
        internalStatusChange,
        isTestMode,
    }: CreateBackgroundCheckHistoryParams,
) {
    const backgroundCheckHistoryStorage = getBackgroundCheckHistoryStorage(context);

    if (stopCheckingForResultsHistoryId) {
        backgroundCheckHistoryStorage.post({
            action: 'partial-update',
            id: stopCheckingForResultsHistoryId,
            data: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                checkResponseByRequestId: null as any, // should work, need to update platform type definitions
            },
        });
    }

    let parsedXml: ParsedBackgroundCheck | undefined = undefined;

    if (responseXml) {
        try {
            parsedXml = parseBackgroundCheck(responseXml);
        } catch (e) {
            console.warn('There was an error when parsing background check');
        }
    }

    backgroundCheckHistoryStorage.post({
        action: 'create',
        data: {
            accountId: accountId,
            type: type,
            prevStatus: backgroundCheckStatus ?? undefined,
            nextStatus: nextStatus,
            requestId: response?.executionId,
            request: request,
            requestedBy: requestedBy,
            checkResponseByRequestId: response?.executionId,
            message: message ?? response?.message,
            responseXml: responseXml ? cleanResults(responseXml) : undefined,
            parsedXml: parsedXml,
            internalStatusChange: internalStatusChange,
            isTestMode: isTestMode,
        },
    });
}
